import type { CoachPreflightOutcome } from '../app/coach';

/**
 * B-19 — **the pre-flight's pure vocabulary** (design spec §2.6).
 *
 * Split out of the More tab's own decisions because §2.6 gives the chip **two surfaces**: the
 * More tab, and match setup when the purpose is a team match. One copy of the six sentences, so
 * the answer a coach reads the night before and the answer they read at the pitch can never be
 * two different answers.
 *
 * ── B-24: one copy of the sentences, two truths about *where* ────────────────
 *
 * The sentences were written on the More tab, where the credential field sits directly above the
 * chip, and two of them said so — *"Paste one above"*. On match setup there is no field above, or
 * anywhere near it, so that sentence sent a user hunting for a control that is not on the screen.
 * Andrew hit exactly that while connecting his phone at a match: *"I don't see anywhere to
 * configure the credentials."*
 *
 * So the two sentences that point somewhere take {@link PreflightSurfaceB} and name the place that
 * is really there from where the chip is being read. The parameter is **required** rather than
 * defaulted on purpose: a default is how a third surface would silently inherit the More tab's
 * *"above"* and re-open this defect without a single line changing here.
 *
 * DOM-free — `more/moreB.test.ts` drives every arm on every surface.
 */

/**
 * What the chip is doing right now.
 *
 * `idle` and `testing` are states of the control; `result` carries an outcome the facade
 * actually observed. There is deliberately **no** fourth arm that infers a result from stored
 * configuration — the chip reports the branch a real request took, or it reports nothing.
 */
export type PreflightRunB =
  | { readonly kind: 'idle' }
  | { readonly kind: 'testing' }
  | { readonly kind: 'result'; readonly outcome: CoachPreflightOutcome };

/** How loudly a result reads. Never the only channel — the words carry the meaning. */
export type PreflightToneB = 'ready' | 'warn' | 'problem';

/**
 * Which screen the chip is being read on — §2.6's two surfaces, named.
 *
 * Not cosmetic: it is the difference between a sentence that points at a field the reader can see
 * and one that points at a field that is on another tab.
 */
export type PreflightSurfaceB = 'more' | 'setup';

/**
 * Where the service-credential field actually is, said from each surface.
 *
 * `more` — `CredentialCardB` renders the field (or its *Replace* button) directly above the chip.
 * `setup` — there is no credential control on match setup at all, so the only true answer names
 * the tab and the card: More → *Coach Claude*.
 */
const CREDENTIAL_IS_AT: Readonly<Record<PreflightSurfaceB, string>> = {
  more: 'above',
  setup: 'in the More tab, under Coach Claude',
};

export interface PreflightNoticeB {
  readonly tone: PreflightToneB;
  /** The chip's own word — short enough to read at a glance. */
  readonly label: string;
  /** The sentence under it: what is true, and **where the fix is**. */
  readonly detail: string;
}

/**
 * §2.6 / §5.4 — one outcome, its word, and its sentence, said from one surface.
 *
 * ── Why the two "not configured" arms read completely differently ────────────
 *
 * They are the same phrase about two different machines, and sending the user to the wrong one
 * is the failure this whole surface exists to prevent:
 *
 * - `notConfigured` — *this phone* holds no credential. The fix is the credential field, and
 *   `surface` decides whether that field is above the reader or on another tab.
 * - `workerUnconfigured` — the service answered, **accepted this credential**, and has no Coach
 *   Claude key of its own. Nothing the user can do on the phone will change that, so the copy
 *   says so rather than sending them to re-enter a credential that just worked.
 *
 * `unreachable` is the only sentence permitted to mention signal, and `ready` is the only one
 * permitted to say the service is set up — both because a real response said so.
 *
 * ── B-24: `refused` may not ask for an inspection this app cannot give ───────
 *
 * It used to say *"Check it was pasted whole, with no spaces"*. Nothing in Touchline can show what
 * was entered: the field is masked, there is no reveal, and a stored credential is described and
 * never echoed. So the sentence asked for a check the interface makes impossible. It now names the
 * only thing that is actually on offer — replace it — and says plainly that the value is never
 * shown back, which is *why* re-entering is the move. It must never imply that the card's
 * *"Saved on this device · N characters"* line verifies the value: that line verifies **length**,
 * and a mistyped or transposed character keeps the length exactly.
 */
export function preflightNoticeB(
  outcome: CoachPreflightOutcome,
  surface: PreflightSurfaceB,
): PreflightNoticeB {
  switch (outcome.kind) {
    case 'ready':
      return {
        tone: 'ready',
        label: 'Connected',
        detail: 'Coach Claude answered. The connection and this credential both work.',
      };
    case 'notConfigured':
      return {
        tone: 'warn',
        label: 'Not set up',
        detail: `There is no service credential on this device yet, so nothing was sent. Enter one ${CREDENTIAL_IS_AT[surface]}, then test again.`,
      };
    case 'workerUnconfigured':
      return {
        tone: 'warn',
        label: 'Service unfinished',
        detail:
          'The service answered and accepted this credential, but it has no Coach Claude key of its own yet. That is a setting on the service, not on this phone.',
      };
    case 'refused':
      return {
        tone: 'problem',
        label: 'Refused',
        detail: `The service refused this credential. It is never shown back to you, so replace it ${CREDENTIAL_IS_AT[surface]}, then test again.`,
      };
    case 'serverError':
      return {
        tone: 'problem',
        label: 'Service problem',
        detail:
          outcome.status === null
            ? 'The service answered with something this app could not read. Nothing is wrong with your phone — try again shortly.'
            : `The service answered with a problem (status ${String(outcome.status)}). Nothing is wrong with your phone — try again shortly.`,
      };
    case 'unreachable':
      return {
        tone: 'problem',
        label: 'No answer',
        detail:
          'Nothing answered — most likely no signal here. Recording a match never needs the network; only Coach Claude does.',
      };
  }
}

